import { Injectable } from '@angular/core';
import { recomendationsService } from '../recomendations/recomendations.service';
import { NewRecomendationsService } from '../new-recomendations/new-recomendations.service';
import { DesignService } from '../design/design.service';

/**
 * Single entry point for "open the recommendations popup".
 *
 * `TemplateData.NewDesign` (DesignService) decides which of the two popups a
 * brand gets: the redesigned `app-new-recomendations` (on; loaded lazily —
 * `NewRecomendationsService.open()` mounts it and waits until it listens) or
 * the legacy `app-recomendations` (off / missing). Every checkout trigger —
 * menu, legacy header, new-header, side-cart, new-side-cart, favourites,
 * product-details, legacy checkout's promotion items — calls `open()` so the
 * switch is honoured on every page, and `app.component.html` mounts only the
 * matching component.
 */
@Injectable({
  providedIn: 'root',
})
export class RecomendationsSwitchService {
  /**
   * Set by open({ oncePerVisit: true }) for the popup that handles this open.
   * The cart drawer's Checkout passes it: the first Checkout after a page load
   * shows the recommendations, later ones go straight to checkout. Other
   * triggers don't pass it and behave as before.
   */
  private oncePerVisit = false;

  /**
   * The popup has been shown since this page load. Kept in memory on purpose:
   * it survives navigating around the app (root service) and resets on every
   * refresh — nothing is written to storage.
   */
  private seenThisLoad = false;

  constructor(
    private design: DesignService,
    private legacy: recomendationsService,
    private redesigned: NewRecomendationsService
  ) {}

  /** Fires whichever popup the brand is configured for. */
  open(opts?: { oncePerVisit?: boolean }) {
    this.oncePerVisit = !!opts?.oncePerVisit;
    if (this.design.isNew) {
      this.redesigned.open();
    } else {
      this.legacy.showRecomendations.next();
    }
  }

  /**
   * Asked by both popups at their "show or go straight to checkout" step:
   * true when this open asked for once-per-visit and the popup has already
   * been shown since the page loaded. Their own guards (checkout off,
   * delivery type stopped, store login) and checkout routing still run.
   */
  skipThisTime(): boolean {
    return this.oncePerVisit && this.seenThisLoad;
  }

  /** Called by the popups when they actually show. */
  markSeen() {
    this.seenThisLoad = true;
  }

  /**
   * Called by both checkouts once an order is placed, so the next basket gets
   * its own first Checkout with recommendations — the success screen doesn't
   * reload the page, so the in-memory flag would otherwise carry over.
   */
  resetSeen() {
    this.seenThisLoad = false;
  }
}
